import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "reactstrap";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronRight, faEllipsisH, faHome } from "@fortawesome/free-solid-svg-icons";
import { resolveBreadcrumbs } from "../../helpers/breadcrumbs/resolveBreadcrumbs";

// Altura de la barra superior según el tema ($top-nav-height); se mide en
// vivo porque en móvil y con el menú colapsado cambia.
const DEFAULT_NAVBAR_HEIGHT = 69;

// HO-TASK-250: barra de migas de pan + "Volver" para todas las vistas del
// panel (manager, propietario, seguridad y root). Se monta una sola vez por
// layout, justo encima de las rutas (en el dashboard, debajo del banner de la
// tasa de cambio), y se arma solo a partir de la URL, así que una pantalla
// nueva solo necesita su entrada en breadcrumbRoutes.
//
// Es sticky: queda pegada debajo de la barra superior (que también lo es)
// cuando la pantalla se desplaza, con el mismo fondo translúcido del tema
// (navbar-glass) para que el contenido se lea al pasar por debajo.
//
// "Volver" apunta al nivel inmediatamente superior de la ruta (no a
// history.goBack) para que siempre lleve a un lugar predecible, aunque se
// haya entrado por un link directo o desde una notificación.
//
// Los links llevan consigo el location.state actual: varias pantallas
// (gastos comunes/particulares, áreas, vigilantes) reciben por ahí datos que
// no están en la URL, y sus hijas (crear, editar) reciben ese mismo objeto,
// así que al subir un nivel lo devolvemos tal cual.
//
// Responsive: por debajo de md la ruta completa no cabe en una línea y se
// partía dejando chevrones sueltos al inicio de cada fila. En pantallas
// chicas se muestran solo el nivel anterior y el actual; los niveles previos
// se pliegan en un "…" que los despliega al tocarlo.
const Breadcrumbs = () => {
  const { pathname, state } = useLocation();
  const role = useSelector((store) => store.auth?.user?.role);
  const crumbs = resolveBreadcrumbs(pathname, { role, state });

  const barRef = useRef(null);
  const [navbarHeight, setNavbarHeight] = useState(DEFAULT_NAVBAR_HEIGHT);
  const [isStuck, setIsStuck] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [pathname]);

  useEffect(() => {
    const measure = () => {
      const navbar = document.querySelector(".navbar-top");
      setNavbarHeight(navbar?.offsetHeight || DEFAULT_NAVBAR_HEIGHT);
    };
    const onScroll = () => {
      if (!barRef.current) return;
      // Cuando la barra llega a tocar la barra superior está "pegada": ahí
      // le ponemos sombra para separarla del contenido que pasa por debajo.
      setIsStuck(barRef.current.getBoundingClientRect().top <= navbarHeight + 1);
    };

    measure();
    onScroll();
    // La barra superior puede cambiar de alto sin que cambie la ventana (al
    // cargar la fuente o el tema, o si sus elementos pasan a otra línea). Si
    // solo se midiera al montar, las migas quedaban pegadas a una altura vieja,
    // flotando sobre el contenido.
    const navbar = document.querySelector(".navbar-top");
    const observer =
      navbar && typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(navbar);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", onScroll);
    };
  }, [navbarHeight, pathname]);

  // En la pantalla de inicio la única miga sería "Inicio": no hay a dónde volver.
  if (crumbs.length < 2) return null;

  const parent = crumbs[crumbs.length - 2];
  // Índice del primer nivel que se ve siempre en móvil (el padre directo).
  const firstMobileIndex = crumbs.length - 2;
  const hasCollapsed = firstMobileIndex > 0;

  return (
    <div
      ref={barRef}
      className={classNames("hl-breadcrumbs-bar navbar-glass d-flex align-items-center py-1 mb-2", {
        "navbar-glass-shadow": isStuck,
      })}
      style={{
        position: "sticky",
        top: navbarHeight,
        // Por encima del contenido en flujo normal, incluidos los switches y
        // checkboxes: Bootstrap le da z-index 1 a todo .custom-control, asi
        // que con la barra tambien en 1 cualquiera de ellos le pasaba por
        // encima al estar despues en el DOM (se veia en Reportes, con el
        // interruptor de bolivares montado sobre las migas).
        //
        // Queda debajo de lo que se abre sobre la pagina: el menu de periodos
        // (5) y los desplegables del tema (perfil, notificaciones: 1000+).
        zIndex: 3,
        marginLeft: -15,
        marginRight: -15,
        paddingLeft: 15,
        paddingRight: 15,
      }}
    >
      <Button
        tag={Link}
        to={{ pathname: parent.to, state }}
        color="falcon-default"
        className="rounded-circle p-0 d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
        style={{ width: "2rem", height: "2rem" }}
        title={`Volver a ${parent.label}`}
        aria-label={`Volver a ${parent.label}`}
      >
        <FontAwesomeIcon icon={faArrowLeft} />
      </Button>
      <nav aria-label="Ruta de navegación" className="flex-grow-1" style={{ minWidth: 0 }}>
        <ol className="list-unstyled d-flex flex-wrap align-items-center mb-0">
          {hasCollapsed && !expanded && (
            <li className="d-flex d-md-none align-items-center">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-500"
                onClick={() => setExpanded(true)}
                aria-label="Mostrar la ruta completa"
                title="Mostrar la ruta completa"
              >
                <FontAwesomeIcon icon={faEllipsisH} />
              </button>
              <FontAwesomeIcon icon={faChevronRight} className="text-300 mx-2" transform="shrink-7" />
            </li>
          )}
          {crumbs.map((crumb, index) => {
            const isFirst = index === 0;
            const isCurrent = index === crumbs.length - 1;
            // En móvil solo se ven el padre y el actual, salvo que se despliegue.
            const collapsedOnMobile = index < firstMobileIndex && !expanded;
            // El chevrón va delante de cada nivel menos del primero visible.
            const showChevronOnMobile = index > firstMobileIndex || expanded;
            return (
              <li
                key={crumb.key}
                className={classNames("align-items-center", {
                  "d-none d-md-flex": collapsedOnMobile,
                  "d-flex": !collapsedOnMobile,
                })}
                aria-current={isCurrent ? "page" : undefined}
              >
                {!isFirst && (
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className={classNames("text-300 mx-2", {
                      "d-none d-md-inline-block": !showChevronOnMobile,
                    })}
                    transform="shrink-7"
                  />
                )}
                {isCurrent ? (
                  <span className="fs-0 font-weight-semi-bold text-900">{crumb.label}</span>
                ) : (
                  <Link to={{ pathname: crumb.to, state }} className="fs--1 text-500">
                    {isFirst && <FontAwesomeIcon icon={faHome} className="mr-1 text-primary" />}
                    {crumb.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default Breadcrumbs;
